import { useEffect, useRef, useState } from "react";

import { Link, useNavigate } from "react-router-dom";
import { useSidebar } from "../context/SidebarContext";
import { useAuth } from "../context/AuthContext";
// import { ThemeToggleButton } from "../components/common/ThemeToggleButton";
import NotificationDropdown from "../components/header/NotificationDropdown";
import UserDropdown from "../components/header/UserDropdown";
import { ChevronDownIcon } from "../icons";
import EpcHeaderEmblem from "../components/EpcHeaderEmblem";

const AppHeader: React.FC = () => {
  const [isApplicationMenuOpen, setApplicationMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isDepartmentDropdownOpen, setIsDepartmentDropdownOpen] = useState(false);

  const { isExpanded, isMobileOpen, toggleSidebar, toggleMobileSidebar } = useSidebar();
  const { activeDepartment, availableDepartments, switchDepartment } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const departmentDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (window.innerWidth >= 1024) {
      toggleSidebar();
    } else {
      toggleMobileSidebar();
    }
  };

  const toggleApplicationMenu = () => {
    setApplicationMenuOpen(!isApplicationMenuOpen);
  };

  // Comprehensive search data - includes main menus and submenus with correct paths
  const searchData = [
    // Main Navigation
    { id: '1', title: 'Dashboard', subtitle: 'Main Dashboard', type: 'menu', path: '/dashboard', category: 'Navigation' },
    { id: '2', title: 'Projects', subtitle: 'Manage Projects', type: 'menu', path: '/projects', category: 'Navigation' },
    { id: '3', title: 'Admin Panel', subtitle: 'Administrative functions', type: 'menu', path: '/admin', category: 'Navigation' },
    { id: '4', title: 'Masters', subtitle: 'Master data management', type: 'menu', path: '/masters', category: 'Navigation' },

    // Project Management
    { id: '5', title: 'Project Management', subtitle: 'Manage all projects', type: 'submenu', path: '/projects/projectManagment', category: 'Projects' },
    { id: '6', title: 'Project List', subtitle: 'View all projects', type: 'submenu', path: '/projects', category: 'Projects' },

    // User Management (Admin)
    { id: '7', title: 'User Management', subtitle: 'Manage system users', type: 'submenu', path: '/admin/users', category: 'User Management' },
    { id: '8', title: 'Roles', subtitle: 'Manage user roles', type: 'submenu', path: '/admin/roles', category: 'User Management' },
    { id: '9', title: 'Features', subtitle: 'Manage system features', type: 'submenu', path: '/admin/features', category: 'User Management' },

    // Master Data
    { id: '14', title: 'Asset Catalog', subtitle: 'Manage project types & assets', type: 'submenu', path: '/masters/asset-catalog', category: 'Master Data' },
    { id: '15', title: 'Departments', subtitle: 'EPC functional department master', type: 'submenu', path: '/masters/departments', category: 'Master Data' },
    { id: '11', title: 'Locations', subtitle: 'Manage locations', type: 'submenu', path: '/masters/locations', category: 'Master Data' },
    { id: '12', title: 'Manufacturers', subtitle: 'Manage manufacturers', type: 'submenu', path: '/masters/manufacturers', category: 'Master Data' },
    { id: '13', title: 'Customers', subtitle: 'Manage customers', type: 'submenu', path: '/masters/customers', category: 'Master Data' },
    { id: '16', title: 'Checkpoints', subtitle: 'Manage checkpoints', type: 'submenu', path: '/masters/checkpoints', category: 'Master Data' },

    // Checkpoint Management
    { id: '19', title: 'Checkpoint List', subtitle: 'View all checkpoints', type: 'submenu', path: '/masters/checkpoints', category: 'Checkpoints' },
    { id: '20', title: 'Bulk Checkpoint Form', subtitle: 'Create checkpoints in bulk', type: 'submenu', path: '/masters/checkpoints/bulk', category: 'Checkpoints' },
    { id: '21', title: 'Bulk Upload Checkpoints', subtitle: 'Upload checkpoints in bulk', type: 'submenu', path: '/masters/checkpoints/bulk-upload', category: 'Checkpoints' },

    // Quick Actions
    { id: '22', title: 'Profile', subtitle: 'View my profile', type: 'action', path: '/profile', category: 'Quick Actions' },
    { id: '23', title: 'Sign Out', subtitle: 'Sign out of the system', type: 'action', path: '/signin', category: 'Quick Actions' },
  ];

  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.length > 0) {
      const filtered = searchData.filter(item =>
        item.title.toLowerCase().includes(query.toLowerCase()) ||
        item.subtitle.toLowerCase().includes(query.toLowerCase())
      );
      setSearchResults(filtered);
      setShowAutocomplete(true);
      setSelectedIndex(-1);
    } else {
      setShowAutocomplete(false);
      setSearchResults([]);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showAutocomplete) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && searchResults[selectedIndex]) {
          handleResultClick(searchResults[selectedIndex], e);
        }
        break;
      case 'Escape':
        setShowAutocomplete(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Handle result selection
  const handleResultClick = (result: any, event?: React.MouseEvent | React.KeyboardEvent) => {
    // Prevent default behavior
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Navigate to the selected menu item
    if (result.path) {
      navigate(result.path);
    }

    // Close autocomplete and clear search
    setShowAutocomplete(false);
    setSearchQuery('');
    setSelectedIndex(-1);

    // Blur the input to remove focus
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  // Close autocomplete when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowAutocomplete(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut (Ctrl/Cmd + K)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Close department dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (departmentDropdownRef.current && !departmentDropdownRef.current.contains(event.target as Node)) {
        setIsDepartmentDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDepartmentSwitch = async (department: string) => {
    if (department === activeDepartment) {
      setIsDepartmentDropdownOpen(false);
      return;
    }
    
    try {
      await switchDepartment(department);
      setIsDepartmentDropdownOpen(false);
      // Optional: Refresh page or navigate to dashboard to ensure clean state
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to switch department:', error);
    }
  };

  return (
    <header
      className="sticky top-0 z-[35] flex w-full backdrop-blur-md lg:h-[76px] lg:items-center"
      style={{
        background: '#F8F2E2',
        borderBottom: '1px solid rgba(11,59,46,0.16)',
        boxShadow: '0 2px 12px -8px rgba(11,59,46,0.22)',
      }}
    >
      {/* ── EPC engraved solar-sun + wind-turbine watermark + champagne gleam (decorative) ── */}
      <span aria-hidden className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <EpcHeaderEmblem className="absolute right-[12.5rem] top-1/2 hidden -translate-y-1/2 xl:block" style={{ opacity: 0.9 }} />
        <span className="se-hdr-gleam absolute inset-x-0 bottom-0 h-px" />
      </span>
      <style>{`
        .se-hdr-gleam{background:linear-gradient(90deg,transparent 0%,rgba(201,169,97,0) 25%,rgba(201,169,97,0.6) 50%,rgba(201,169,97,0) 75%,transparent 100%);background-size:220% 100%;animation:seHdrGleam 7s linear infinite;}
        @keyframes seHdrGleam{0%{background-position:120% 0}100%{background-position:-120% 0}}
        @media (prefers-reduced-motion:reduce){.se-hdr-gleam{animation:none;}}
      `}</style>
      <div className="relative z-10 flex flex-col items-center justify-between grow lg:h-full lg:flex-row lg:px-6">
        <div className="flex items-center justify-between w-full gap-2 px-3 py-2 border-b border-[rgba(11,59,46,0.14)] sm:gap-4 lg:justify-normal lg:border-b-0 lg:px-0 lg:py-0">
          <button
            className="flex items-center justify-center w-10 h-10 rounded-lg z-50 lg:h-11 lg:w-11 transition-colors text-[#1F5A47] hover:bg-[rgba(11,59,46,0.06)]"
            onClick={handleToggle}
            aria-label="Toggle Sidebar"
          >
            {(isMobileOpen || (window.innerWidth >= 1024 && !isExpanded)) ? (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M6.21967 7.28131C5.92678 6.98841 5.92678 6.51354 6.21967 6.22065C6.51256 5.92775 6.98744 5.92775 7.28033 6.22065L11.999 10.9393L16.7176 6.22078C17.0105 5.92789 17.4854 5.92788 17.7782 6.22078C18.0711 6.51367 18.0711 6.98855 17.7782 7.28144L13.0597 12L17.7782 16.7186C18.0711 17.0115 18.0711 17.4863 17.7782 17.7792C17.4854 18.0721 17.0105 18.0721 16.7176 17.7792L11.999 13.0607L7.28033 17.7794C6.98744 18.0722 6.51256 18.0722 6.21967 17.7794C5.92678 17.4865 5.92678 17.0116 6.21967 16.7187L10.9384 12L6.21967 7.28131Z"
                  fill="currentColor"
                />
              </svg>
            ) : (
              <svg
                width="16"
                height="12"
                viewBox="0 0 16 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0.583252 1C0.583252 0.585788 0.919038 0.25 1.33325 0.25H14.6666C15.0808 0.25 15.4166 0.585786 15.4166 1C15.4166 1.41421 15.0808 1.75 14.6666 1.75L1.33325 1.75C0.919038 1.75 0.583252 1.41422 0.583252 1ZM0.583252 11C0.583252 10.5858 0.919038 10.25 1.33325 10.25L14.6666 10.25C15.0808 10.25 15.4166 10.5858 15.4166 11C15.4166 11.4142 15.0808 11.75 14.6666 11.75L1.33325 11.75C0.919038 11.75 0.583252 11.4142 0.583252 11ZM1.33325 5.25C0.919038 5.25 0.583252 5.58579 0.583252 6C0.583252 6.41421 0.919038 6.75 1.33325 6.75L7.99992 6.75C8.41413 6.75 8.74992 6.41421 8.74992 6C8.74992 5.58579 8.41413 5.25 7.99992 5.25L1.33325 5.25Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </button>

          <Link to="/" className="lg:hidden">
            <img
              className="dark:hidden"
              src="./images/logo/logo.svg"
              alt="Logo"
            />
            <img
              className="hidden dark:block"
              src="./images/logo/logo.svg"
              alt="Logo"
            />
          </Link>

          <button
            onClick={toggleApplicationMenu}
            className="flex items-center justify-center w-10 h-10 rounded-lg z-99999 lg:hidden text-[#1F5A47] hover:bg-[rgba(11,59,46,0.06)]"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.99902 10.4951C6.82745 10.4951 7.49902 11.1667 7.49902 11.9951V12.0051C7.49902 12.8335 6.82745 13.5051 5.99902 13.5051C5.1706 13.5051 4.49902 12.8335 4.49902 12.0051V11.9951C4.49902 11.1667 5.1706 10.4951 5.99902 10.4951ZM17.999 10.4951C18.8275 10.4951 19.499 11.1667 19.499 11.9951V12.0051C19.499 12.8335 18.8275 13.5051 17.999 13.5051C17.1706 13.5051 16.499 12.8335 16.499 12.0051V11.9951C16.499 11.1667 17.1706 10.4951 17.999 10.4951ZM13.499 11.9951C13.499 11.1667 12.8275 10.4951 11.999 10.4951C11.1706 10.4951 10.499 11.1667 10.499 11.9951V12.0051C10.499 12.8335 11.1706 13.5051 11.999 13.5051C12.8275 13.5051 13.499 12.8335 13.499 12.0051V11.9951Z"
                fill="currentColor"
              />
            </svg>
          </button>

          <div className="hidden lg:block">
            <form onSubmit={(e) => e.preventDefault()}>
              {/* Search input code... */}
              <div className="relative">
                {/* ... existing search code ... */}
                <span className="absolute -translate-y-1/2 pointer-events-none left-4 top-1/2">
                  <svg
                    className="fill-[#C9A961]"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                      fill=""
                    />
                  </svg>
                </span>
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Search menus, projects, or actions..."
                  className="h-11 w-full rounded-xl border border-[rgba(11,59,46,0.22)] bg-[#fcfaf3] py-2.5 pl-12 pr-16 text-sm text-[#0B3B2E] shadow-sm outline-none transition placeholder:text-[#7C8A82] focus:border-[#0B3B2E] focus:ring-2 focus:ring-[rgba(166,206,57,0.30)] xl:w-[460px]"
                />

                <button
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 rounded-md border border-[rgba(11,59,46,0.18)] bg-[#F8F2E2] px-2 py-1 text-xs font-semibold text-[#1F5A47] shadow-sm"
                  aria-label="Keyboard shortcut"
                >
                  <kbd className="font-sans">Ctrl</kbd>
                  <span className="text-[#7C8A82]">+</span>
                  <kbd className="font-sans">K</kbd>
                </button>
              </div>

              {/* Autocomplete Dropdown */}
              {showAutocomplete && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 rounded-xl z-50 max-h-80 overflow-y-auto" style={{ background: 'rgba(252,251,246,0.98)', border: '1px solid rgba(11,59,46,0.2)', boxShadow: '0 18px 44px -20px rgba(11,59,46,0.4)', backdropFilter: 'blur(8px)' }}>
                  {(() => {
                    // Group results by category
                    const groupedResults = searchResults.reduce((acc: Record<string, any[]>, result: any) => {
                      if (!acc[result.category]) {
                        acc[result.category] = [];
                      }
                      acc[result.category].push(result);
                      return acc;
                    }, {});

                    return Object.entries(groupedResults).map(([category, results]) => (
                      <div key={category}>
                        {/* Category Header */}
                        <div className="px-4 py-2 text-[10px] font-bold uppercase tracking-[0.14em] border-b" style={{ color: '#8A5A08', background: 'rgba(201,169,97,0.10)', borderColor: 'rgba(11,59,46,0.10)' }}>
                          {category}
                        </div>

                        {/* Category Results */}
                        {(results as any[]).map((result: any) => {
                          const globalIndex = searchResults.indexOf(result);
                          return (
                            <div
                              key={result.id}
                              onClick={(e) => handleResultClick(result, e)}
                              className={`px-4 py-3 cursor-pointer transition-colors hover:bg-[rgba(11,59,46,0.05)] ${globalIndex === selectedIndex ? 'bg-[rgba(201,169,97,0.14)] border-l-2 border-[#1F5A47]' : ''
                                }`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${result.type === 'menu' ? 'bg-[rgba(31,90,71,0.12)]' :
                                  result.type === 'submenu' ? 'bg-[rgba(51,146,116,0.14)]' :
                                    'bg-[rgba(201,169,97,0.18)]'
                                  }`}>
                                  {result.type === 'menu' ? (
                                    <svg className="w-4 h-4 text-[#1F5A47]" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm8 0a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1V8zm0 4a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1v-2z" clipRule="evenodd" />
                                    </svg>
                                  ) : result.type === 'submenu' ? (
                                    <svg className="w-4 h-4 text-[#339274]" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                    </svg>
                                  ) : (
                                    <svg className="w-4 h-4 text-[#8A5A08]" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="font-semibold text-[#0B3B2E]">{result.title}</div>
                                  <div className="text-sm text-[#7C8A82]">{result.subtitle}</div>
                                </div>
                                <div className="text-xs uppercase tracking-wider text-[#7C8A82]">
                                  {result.type}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ));
                  })()}
                </div>
              )}

              {/* No Results */}
              {showAutocomplete && searchResults.length === 0 && searchQuery.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 rounded-xl z-50 p-4" style={{ background: 'rgba(252,251,246,0.98)', border: '1px solid rgba(11,59,46,0.2)', boxShadow: '0 18px 44px -20px rgba(11,59,46,0.4)' }}>
                  <div className="text-center" style={{ color: '#7C8A82' }}>
                    <svg className="w-8 h-8 mx-auto mb-2" style={{ color: 'rgba(11,59,46,0.25)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <p className="text-sm">No results found for "{searchQuery}"</p>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
        <div
          className={`${isApplicationMenuOpen ? "flex" : "hidden"
            } items-center justify-between w-full gap-4 px-5 py-4 lg:flex lg:py-0 shadow-theme-md lg:justify-end lg:px-0 lg:shadow-none`}
        >
          <div className="flex items-center gap-2 2xsm:gap-3">
            {/* Department Selector */}
            {availableDepartments && availableDepartments.length > 1 && (
              <div className="relative" ref={departmentDropdownRef}>
                <button
                  onClick={() => setIsDepartmentDropdownOpen(!isDepartmentDropdownOpen)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg transition-colors text-[#0B3B2E] bg-[#fcfaf3] border border-[rgba(11,59,46,0.2)] hover:bg-[rgba(11,59,46,0.05)]"
                >
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: '#339274' }}></span>
                    {activeDepartment}
                  </span>
                  <ChevronDownIcon className={`w-4 h-4 transition-transform ${isDepartmentDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isDepartmentDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 rounded-xl z-50 py-1" style={{ background: 'rgba(252,251,246,0.98)', border: '1px solid rgba(11,59,46,0.2)', boxShadow: '0 18px 44px -20px rgba(11,59,46,0.4)' }}>
                    <div className="px-4 py-2 text-[10px] font-bold uppercase tracking-[0.14em] border-b" style={{ color: '#8A5A08', borderColor: 'rgba(11,59,46,0.1)' }}>
                      Switch Department
                    </div>
                    {availableDepartments.map((dept) => (
                      <button
                        key={dept}
                        onClick={() => handleDepartmentSwitch(dept)}
                        className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors hover:bg-[rgba(11,59,46,0.05)] ${
                          activeDepartment === dept
                            ? 'font-semibold text-[#0B3B2E] bg-[rgba(201,169,97,0.14)]'
                            : 'text-[#2A1F17]'
                        }`}
                      >
                        <span className="w-2 h-2 rounded-full" style={{ background: activeDepartment === dept ? '#339274' : 'rgba(11,59,46,0.25)' }}></span>
                        {dept}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* <!-- Dark Mode Toggler --> */}
            {/* <ThemeToggleButton /> */}
            {/* <!-- Dark Mode Toggler --> */}
            <NotificationDropdown />
            {/* <!-- Notification Menu Area --> */}
          </div>
          {/* <!-- User Area --> */}
          <UserDropdown />
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
